import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useApiInfoContextValueHook } from "../../../../../contexts/ApiInfoContext";
import { SellertoolSignatureUtils } from "../../../../../utils/sellertool-signature-utils";
import { ShoppingCartItemsApi } from "../../../../../apis/ShoppingCartItemsApi";
import { useEffect } from "react";
import { CustomBackdropController } from "../../../../../components/backdrop/default/v1";
import { customToast } from "../../../../../components/toast/custom-react-toastify/v1";

const QUERY_KEY = 'shoppingCartItems';

export function useShoppingCartItemsQuery({
    shoppingCartStageId
}) {
    const apiInfoContextValueHook = useApiInfoContextValueHook();

    const queryResult = useQuery({
        queryKey: [QUERY_KEY, shoppingCartStageId],
        enabled: !!(apiInfoContextValueHook?.apiInfo?.apiKey && apiInfoContextValueHook?.apiInfo?.secretKey && shoppingCartStageId),
        queryFn: async () => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }
            const fetchResult = await ShoppingCartItemsApi().searchList({
                params: {
                    shoppingCartStageId: shoppingCartStageId
                },
                headers: headers
            })

            return fetchResult;
        }
    });

    useEffect(() => {
        if (queryResult.isLoading) {
            CustomBackdropController().showBackdrop();
        } else {
            CustomBackdropController().hideBackdrop();
        }
    }, [queryResult.isLoading]);

    return queryResult;
}

export function useCreateShoppingCartItemsMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await ShoppingCartItemsApi().createList({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('장바구니에 상품이 추가 되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function usePatchShoppingCartItemsMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await ShoppingCartItemsApi().patchBatch({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('성공적으로 수정되었습니다.', {
                autoClose: 1000
            });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useDeleteShoppingCartItemMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await ShoppingCartItemsApi().deleteOne({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('성공적으로 삭제되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}

export function useDeleteShoppingCartItemsMutation() {
    const apiInfoContextValueHook = useApiInfoContextValueHook();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ body }) => {
            const timestamp = Date.now().toString();
            const signature = SellertoolSignatureUtils().generateSigniture({
                apiKey: apiInfoContextValueHook?.apiInfo?.apiKey,
                secretKey: apiInfoContextValueHook?.apiInfo?.secretKey,
                timestamp: timestamp
            })

            const headers = {
                'x-sellertool-apiKey': apiInfoContextValueHook?.apiInfo?.apiKey,
                'x-sellertool-timestamp': timestamp,
                'x-sellertool-signiture': signature
            }

            const response = await ShoppingCartItemsApi().deleteByShoppingCartStageId({
                headers: headers,
                body: body
            })

            return response;
        },
        onSuccess: (data) => {
            customToast.success('성공적으로 삭제되었습니다.');
            queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        },
        onError: (error, variables, context) => {
            if (!error) {
                customToast.error('서버와의 연결이 원할하지 않습니다.');
                return;
            }

            let res = error.response;

            customToast.error(res?.data?.message);
        },
        onMutate: async () => {
            CustomBackdropController().showBackdrop();
        },
        onSettled: async () => {
            CustomBackdropController().hideBackdrop();
        }
    });
}